// 同一SKUに複数仕入先（Stocky parity v2 Step 6）— 1商品の仕入先別条件を編集する。
//
// 相見積もり運用の店は同じ商品を複数社から引く。ここで社ごとに単価・入数・
// 最低発注数量・リードタイムを入れて比較し、1社を「既定」にする。
//
// 🔴 既定にした会社だけが発注書ドラフトに出る。他社の条件は比較用に持つだけで
// 数量にも金額にも効かない（比較値を入れた瞬間に発注書が変わると事故になる）。
import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  InlineStack,
  Modal,
  Select,
  Spinner,
  Text,
  TextField,
} from "@shopify/polaris";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { listSuppliers } from "../api/purchase_orders";
import {
  canonicalStockyPublicBigIntId,
  isStockyPublicBigIntId,
  type StockyPublicBigIntId,
} from "../api/stocky_public_ids";
import {
  deleteVariantSupplier,
  listVariantSuppliers,
  putVariantSupplier,
  setPreferredSupplier,
  type VariantSupplierLink,
} from "../api/variant_suppliers";
import { usePlan } from "../hooks/usePlan";
import { UpgradeNote } from "./PlanGate";

import { t } from "../lib/i18n";

interface Props {
  open: boolean;
  onClose: () => void;
  variantId: StockyPublicBigIntId;
  title: string;
}

/** 空欄は「未設定」＝サーバーへ null を送る（0 に丸めない）。 */
function toNumberOrNull(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

export function VariantSupplierModal({
  open,
  onClose,
  variantId,
  title,
}: Props) {
  const queryClient = useQueryClient();
  const [addSupplierId, setAddSupplierId] = useState("");

  // D3（2026-08-02 決定）: Pro 未満は「閲覧＋削除」モード。Pro 時代に入れた条件は
  // 降格後も発注書に効き続けるため、見る・外す手段まで塞がない（backend も
  // GET/DELETE は非ゲート）。編集・追加・既定切替だけを隠す。
  // 判定できない間（読込中・取得失敗）は編集UIを出したままにする＝UX 専用ゲートで、
  // 実際の執行は backend の 403（誤って出しても既存の失敗バナーが受ける）。
  const plan = usePlan();
  const canEdit =
    plan.isLoading || plan.isError || plan.allows("multi_supplier");

  const linksQuery = useQuery({
    queryKey: ["variant-suppliers", variantId],
    queryFn: () => listVariantSuppliers(variantId),
    enabled: open,
  });
  const suppliersQuery = useQuery({
    queryKey: ["po-suppliers"],
    queryFn: listSuppliers,
    enabled: open,
  });

  // 既定を変えると `variants.supplier_id` も動く＝発注書ドラフトと発注点一覧の
  // 「どの仕入先の下に並ぶか」が変わる。両方のキャッシュを捨てないと画面が食い違う。
  const invalidateAll = () => {
    void queryClient.invalidateQueries({
      queryKey: ["variant-suppliers", variantId],
    });
    void queryClient.invalidateQueries({ queryKey: ["purchase-orders"] });
    void queryClient.invalidateQueries({ queryKey: ["reorder-policies"] });
  };

  const saveMutation = useMutation({
    mutationFn: (input: {
      supplierId: StockyPublicBigIntId;
      conditions: Parameters<typeof putVariantSupplier>[2];
    }) => putVariantSupplier(variantId, input.supplierId, input.conditions),
    onSuccess: invalidateAll,
  });
  const preferMutation = useMutation({
    mutationFn: (supplierId: StockyPublicBigIntId) =>
      setPreferredSupplier(variantId, supplierId),
    onSuccess: invalidateAll,
  });
  const removeMutation = useMutation({
    mutationFn: (supplierId: StockyPublicBigIntId) =>
      deleteVariantSupplier(variantId, supplierId),
    onSuccess: invalidateAll,
  });

  const links = linksQuery.data ?? [];
  const linkedIds = new Set(
    links.map((link) => canonicalStockyPublicBigIntId(link.supplier_id)),
  );
  const addableSuppliers = (suppliersQuery.data ?? []).filter(
    (supplier) => !linkedIds.has(canonicalStockyPublicBigIntId(supplier.id)),
  );

  const busy =
    saveMutation.isPending ||
    preferMutation.isPending ||
    removeMutation.isPending;
  const failed =
    saveMutation.isError || preferMutation.isError || removeMutation.isError;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("仕入先別の条件 — {title}", { title })}
    >
      <Modal.Section>
        <BlockStack gap="400">
          <Text as="p" tone="subdued">
            {t(
              "発注書に出るのは「既定」にした仕入先だけです。他の仕入先の条件は比較用に保存されるだけで、発注数量にも金額にも影響しません。",
            )}
          </Text>

          {!canEdit ? (
            <UpgradeNote feature="multi_supplier">
              <p>
                {t(
                  "現在のプランでは、保存済みの条件の閲覧と削除のみ利用できます。既定の仕入先の条件は発注書に引き続き適用されます。",
                )}
              </p>
            </UpgradeNote>
          ) : null}

          {failed ? (
            <Banner tone="critical">
              {t("保存に失敗しました。時間をおいて、もう一度お試しください。")}
            </Banner>
          ) : null}

          {linksQuery.isPending ? (
            <Spinner accessibilityLabel={t("読み込み中")} />
          ) : null}

          {linksQuery.isError ? (
            <Banner
              tone="critical"
              action={{
                content: t("再試行"),
                onAction: () => void linksQuery.refetch(),
              }}
            >
              {t("仕入先の条件を取得できませんでした。")}
            </Banner>
          ) : null}

          {!linksQuery.isPending && links.length === 0 ? (
            <Text as="p" tone="subdued">
              {canEdit
                ? t(
                    "この商品にはまだ仕入先の条件がありません。下から仕入先を追加してください。",
                  )
                : t("この商品には仕入先の条件がありません。")}
            </Text>
          ) : null}

          {links.map((link) => (
            <SupplierConditionCard
              key={link.supplier_id}
              link={link}
              disabled={busy}
              canEdit={canEdit}
              onSave={(conditions) =>
                saveMutation.mutate({
                  supplierId: link.supplier_id,
                  conditions,
                })
              }
              onPrefer={() => preferMutation.mutate(link.supplier_id)}
              onRemove={() => removeMutation.mutate(link.supplier_id)}
            />
          ))}

          {canEdit && addableSuppliers.length > 0 ? (
            <InlineStack gap="200" blockAlign="end">
              <Box width="260px">
                <Select
                  label={t("仕入先を追加")}
                  options={[
                    { label: t("選択してください"), value: "" },
                    ...addableSuppliers.map((supplier) => ({
                      label: supplier.name,
                      value: String(supplier.id),
                    })),
                  ]}
                  value={addSupplierId}
                  onChange={setAddSupplierId}
                  disabled={busy}
                />
              </Box>
              <Button
                disabled={busy || addSupplierId === ""}
                onClick={() => {
                  if (!isStockyPublicBigIntId(addSupplierId)) return;
                  saveMutation.mutate({
                    supplierId: canonicalStockyPublicBigIntId(addSupplierId),
                    conditions: {
                      unit_cost: null,
                      pack_size: null,
                      min_order_qty: null,
                      lead_time_days: null,
                    },
                  });
                  setAddSupplierId("");
                }}
              >
                {t("追加")}
              </Button>
            </InlineStack>
          ) : null}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}

interface CardProps {
  link: VariantSupplierLink;
  disabled: boolean;
  /** false＝閲覧＋削除モード（Pro 未満・D3）。入力欄は読み取り専用・保存/既定切替を隠す。 */
  canEdit: boolean;
  onSave: (conditions: {
    unit_cost: string | null;
    pack_size: number | null;
    min_order_qty: number | null;
    lead_time_days: number | null;
  }) => void;
  onPrefer: () => void;
  onRemove: () => void;
}

function SupplierConditionCard({
  link,
  disabled,
  canEdit,
  onSave,
  onPrefer,
  onRemove,
}: CardProps) {
  const [unitCost, setUnitCost] = useState(link.unit_cost ?? "");
  const [packSize, setPackSize] = useState(
    link.pack_size === null ? "" : String(link.pack_size),
  );
  const [minOrderQty, setMinOrderQty] = useState(
    link.min_order_qty === null ? "" : String(link.min_order_qty),
  );
  const [leadTimeDays, setLeadTimeDays] = useState(
    link.lead_time_days === null ? "" : String(link.lead_time_days),
  );

  // サーバー値が変わったら表示を追随させる。`useState` は初期化きりで props の
  // 変化を拾わないため、これが無いと保存成功→refetch 後も古い値が残り
  // 「効いていない」ように見える（VariantRowEditor で実際に起きた事故と同型）。
  useEffect(() => {
    setUnitCost(link.unit_cost ?? "");
    setPackSize(link.pack_size === null ? "" : String(link.pack_size));
    setMinOrderQty(
      link.min_order_qty === null ? "" : String(link.min_order_qty),
    );
    setLeadTimeDays(
      link.lead_time_days === null ? "" : String(link.lead_time_days),
    );
  }, [link.unit_cost, link.pack_size, link.min_order_qty, link.lead_time_days]);

  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center">
          <InlineStack gap="200" blockAlign="center">
            <Text as="h3" variant="headingSm">
              {link.supplier_name ?? t("（名称不明）")}
            </Text>
            {link.is_preferred ? (
              <Badge tone="success">{t("既定")}</Badge>
            ) : null}
          </InlineStack>
          <InlineStack gap="200">
            {link.is_preferred || !canEdit ? null : (
              <Button
                disabled={disabled}
                onClick={onPrefer}
                // 仕入先の数だけ同じ文言のボタンが並ぶ＝読み上げでは区別できない。
                // 会社名を含めて一意にする（E2E がこの名前で相手を特定するのも兼ねる）。
                accessibilityLabel={`${link.supplier_name ?? t("この仕入先")} を既定にする`}
              >
                {t("既定にする")}
              </Button>
            )}
            <Button
              tone="critical"
              variant="plain"
              disabled={disabled}
              onClick={onRemove}
              accessibilityLabel={`${link.supplier_name ?? t("この仕入先")} の条件を削除`}
            >
              {t("削除")}
            </Button>
          </InlineStack>
        </InlineStack>

        <InlineStack gap="200" blockAlign="end" wrap>
          <Box width="120px">
            <TextField
              label={t("単価")}
              type="number"
              autoComplete="off"
              value={unitCost}
              onChange={setUnitCost}
              disabled={disabled || !canEdit}
              min="0"
              helpText={t("空欄=商品の原価")}
            />
          </Box>
          <Box width="110px">
            <TextField
              label={t("入数")}
              type="number"
              autoComplete="off"
              value={packSize}
              onChange={setPackSize}
              disabled={disabled || !canEdit}
              min="1"
              helpText={t("この倍数へ切上")}
            />
          </Box>
          <Box width="130px">
            <TextField
              label={t("最低発注数量")}
              type="number"
              autoComplete="off"
              value={minOrderQty}
              onChange={setMinOrderQty}
              disabled={disabled || !canEdit}
              min="1"
            />
          </Box>
          <Box width="130px">
            <TextField
              label={t("リードタイム（日）")}
              type="number"
              autoComplete="off"
              value={leadTimeDays}
              onChange={setLeadTimeDays}
              disabled={disabled || !canEdit}
              min="0"
            />
          </Box>
          {canEdit ? (
            <Button
              disabled={disabled}
              onClick={() =>
                onSave({
                  unit_cost: unitCost.trim() === "" ? null : unitCost.trim(),
                  pack_size: toNumberOrNull(packSize),
                  min_order_qty: toNumberOrNull(minOrderQty),
                  lead_time_days: toNumberOrNull(leadTimeDays),
                })
              }
            >
              {t("保存")}
            </Button>
          ) : null}
        </InlineStack>
      </BlockStack>
    </Card>
  );
}
